import React, { useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { IconButton, Tab, Tabs } from '@mui/material'
import Close from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import FileCopy from '@mui/icons-material/FileCopy'
import Phone from '@mui/icons-material/Phone'
import { useLocation } from 'react-router-dom'
import Text from '../1elements/Text'
import PreviewImageSlider from '../1elements/PreviewImageSlider'
import Pin from '../1elements/Icons/js/Pin'
import GoBackButton from '../1elements/Buttons/IconButtons/GoBackButton'
import { reExportedColors } from '../1elements'
import FullDetailsAccordion from '../../v2/components/FullDetailsAccordion'
import DataView from '../../v2/components/DataView'
import {
  createLayoutPreviewData,
  getLayoutPreviewTab,
} from '../3modules/Layouts/layoutPreview'
import { getLayoutObjectTypeLabel } from '../3modules/Layouts/layoutTerminology'
import '../../v2/pages/FullDetailsPage/LocationTab/styles.scss'

const tabs = [
  { custom_name: getLayoutObjectTypeLabel('asset'), sys_name: 'tab0' },
  { custom_name: getLayoutObjectTypeLabel('party'), sys_name: 'tab1' },
  { custom_name: getLayoutObjectTypeLabel('service'), sys_name: 'tab2' },
  { custom_name: getLayoutObjectTypeLabel('contract'), sys_name: 'tab3' },
  { custom_name: 'Dashboard', sys_name: 'tab4' },
  { custom_name: 'Dashboard Data', sys_name: 'tab5' },
]

const relatedTabContent = {
  party: {
    heading: 'People for Location Name',
    title: 'Alex Taylor (Manager)',
    subtitle: 'alex.taylor@example.com',
  },
  service: {
    heading: 'Service List for Location Name',
    title: 'Example Service',
    subtitle: 'service@example.com',
  },
  contract: {
    heading: 'Accounts for Location Name',
    title: 'Example Account (Expires: Jan 15, 2027)',
  },
}

const useStyles = makeStyles(() => ({
  previewRoot: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
    overflow: 'auto',
  },
  topBar: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '5px',
  },
  headerWrapper: {
    alignItems: 'center',
    borderRadius: '6px',
    display: 'flex',
    height: '40px',
    minWidth: '650px',
    padding: '8px 2.5px 11px',
  },
  headerSection: {
    alignItems: 'baseline',
    display: 'flex',
    fontWeight: 600,
    height: '20px',
    padding: '0 16px',
    '& span': {
      fontWeight: 'inherit',
    },
  },
  addressSection: {
    cursor: 'default',
    maxWidth: '250px',
  },
  addressText: {
    color: '#393939',
    display: 'inline-block',
    fontSize: '1rem',
    fontWeight: '400 !important',
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerIcon: {
    height: '16px',
    marginRight: '5px',
    width: '16px',
  },
  copyIcon: {
    height: '14px',
    marginLeft: '5px',
    width: '14px',
  },
  tabsRoot: {
    minHeight: 'auto',
  },
  tabsFlexContainer: {
    gap: '2px',
  },
  tab: {
    backgroundColor: '#9A9A9A',
    borderRadius: '6px 6px 0 0',
    boxShadow: '1px 0 5px 0 rgba(0, 0, 0, 0.25)',
    minHeight: '40px',
    minWidth: 82,
    opacity: 1,
    padding: '8px 19px',
    '& p': {
      color: reExportedColors.WHITE,
    },
  },
  activeTab: {
    backgroundColor: '#ED1C24',
    border: '2px solid #8B1811',
    '& p': {
      color: reExportedColors.WHITE,
    },
  },
  indicator: {
    backgroundColor: '#8B1811',
    zIndex: 1,
  },
  scroller: {
    paddingLeft: '3px',
    paddingTop: '5px',
  },
  content: {
    backgroundColor: reExportedColors.WHITE,
    borderRadius: '0 6px 6px 6px',
    boxShadow: '0 0 3px #00000029',
    marginLeft: '3px',
    minHeight: '520px',
    padding: '24px',
    position: 'relative',
  },
  goBackWrapper: {
    position: 'absolute',
    right: 7,
    top: 10,
    zIndex: 2,
  },
  pageHeading: {
    margin: '0 0 24px',
  },
}))

const TabPreview = ({ layout }) => {
  const classes = useStyles()
  const location = useLocation()
  const [expandedCards, setExpandedCards] = useState({})

  let objectType = layout.object_type
  if (!objectType) {
    const queryParams = new URLSearchParams(location.search)
    objectType = queryParams.get('objectType') || 'asset'
  }

  const { cards, values } = useMemo(
    () => createLayoutPreviewData(layout.cards),
    [layout.cards]
  )
  const activeTab = getLayoutPreviewTab(objectType)
  const objectTypeLabel = getLayoutObjectTypeLabel(objectType)

  const setExpanded = (key, id) => {
    setExpandedCards((previous) => ({ ...previous, [key]: Boolean(id) }))
  }

  const renderLocationTab = () => (
    <>
      <h2 className={classes.pageHeading}>Location</h2>
      <IconButton
        aria-label='Edit location data (preview only)'
        className='edit-button'
        onClick={() => {}}
      >
        <EditIcon />
      </IconButton>
      <div className='image-row'>
        <h4>Image</h4>
        <PreviewImageSlider />
      </div>

      {cards.map((card, index) => {
        const key = `${card.title}-${index}`
        return (
          <FullDetailsAccordion
            key={key}
            id={key}
            detailsComponent={
              <DataView
                orientation='horizontal'
                card={card}
                isEdit={false}
                values={values}
              />
            }
            selectCallback={(id) => setExpanded(key, id)}
            expanded={expandedCards[key] !== false}
          >
            <span className='title'>{card.title}</span>
          </FullDetailsAccordion>
        )
      })}
    </>
  )

  const renderRelatedTab = () => {
    const content = relatedTabContent[objectType]
    if (!content) return null

    const entityKey = `preview-${objectType}`
    return (
      <>
        <h2 className={classes.pageHeading}>{content.heading}</h2>
        <IconButton
          aria-label={`Edit ${objectTypeLabel} data (preview only)`}
          className='edit-button'
          onClick={() => {}}
        >
          <EditIcon />
        </IconButton>
        <FullDetailsAccordion
          id={entityKey}
          detailsComponent={
            <div className='tab-edit-form'>
              {cards.map((card, index) => (
                <DataView
                  key={`${card.title}-${index}`}
                  orientation='horizontal'
                  card={card}
                  isEdit={false}
                  values={values}
                />
              ))}
            </div>
          }
          selectCallback={(id) => setExpanded(entityKey, id)}
          expanded={expandedCards[entityKey] !== false}
        >
          <>
            <span className='title'>{content.title}</span>
            {content.subtitle && (
              <>
                <br />
                <span className='subtitle'>{content.subtitle}</span>
              </>
            )}
          </>
        </FullDetailsAccordion>
      </>
    )
  }

  return (
    <div className={classes.previewRoot}>
      <div className={classes.topBar}>
        <div className={classes.headerWrapper}>
          <div className={`pipe thick ${classes.headerSection}`}>
            <Text type='locationHeaderName'>Location Name</Text>
          </div>
          <div
            className={`pipe thick ${classes.headerSection} ${classes.addressSection}`}
          >
            <Pin
              width={13}
              height={15}
              fill='#393939'
              className={classes.headerIcon}
            />
            <span className={classes.addressText}>1234 Mock St, City</span>
          </div>
          <div className={`pipe thick ${classes.headerSection}`}>
            <Phone className={classes.headerIcon} />
            <span>555-0100</span>
          </div>
          <div className={`pipe thick ${classes.headerSection}`}>
            <span>Transit: T12345</span>
            <FileCopy className={classes.copyIcon} />
          </div>
          <div className={`pipe thick ${classes.headerSection}`}>
            <span>Job ID: J98765</span>
            <FileCopy className={classes.copyIcon} />
          </div>
        </div>
        <IconButton aria-label='Close preview' onClick={() => {}}>
          <Close />
        </IconButton>
      </div>

      <Tabs
        value={activeTab}
        onChange={() => {}}
        classes={{
          indicator: classes.indicator,
          root: classes.tabsRoot,
          scroller: classes.scroller,
          flexContainer: classes.tabsFlexContainer,
        }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.sys_name}
            label={<Text type='tabHeader'>{tab.custom_name}</Text>}
            value={tab.sys_name}
            classes={{ root: classes.tab, selected: classes.activeTab }}
          />
        ))}
      </Tabs>

      <div className={classes.content}>
        <GoBackButton className={classes.goBackWrapper} onClick={() => {}} />
        {(objectType === 'asset' || objectType === 'entity') && renderLocationTab()}
        {(objectType === 'party' ||
          objectType === 'service' ||
          objectType === 'contract') &&
          renderRelatedTab()}
        {objectType === 'dashboard' && <p>Dashboard preview</p>}
      </div>
    </div>
  )
}

export default TabPreview
